import { AlarmLevel } from 'icreate/common/models/alarm-level';
import { MotorChannelType } from 'icreate/common/models/channel-type';
import { ChannelSheetCol } from './channel-sheet';
import {
    runningHourDefaultValues,
    flowCounterDefaultValues,
    digitalDefaultRange,
    analogChannelDefaultValues,
    hhAlarmDefaults,
    hAlarmDefaults,
    llAlarmDefaults,
    lAlarmDefaults,
    sensorFailureDefaults,
    motorAlarmDefaults,
    digitalAlarmDefaults,
    feedbackAlarmDefaults,
    systemAlarmDefaults,
    exhaustDefaultValues,
} from 'icreate/common/models/channel-defaults';

type ChannelSheetCellMappingType = Partial<Record<ChannelSheetCol, string | null>>;

export const runningHourDefaults: ChannelSheetCellMappingType = {
    [ChannelSheetCol.MEASURABLE_MAX]: (
        runningHourDefaultValues.measurableMax / runningHourDefaultValues.scale
    ).toString(),
    [ChannelSheetCol.MEASURABLE_MIN]: (
        runningHourDefaultValues.measurableMin / runningHourDefaultValues.scale
    ).toString(),
    [ChannelSheetCol.SCALE]: runningHourDefaultValues.scale.toString(),
    [ChannelSheetCol.UNIT]: runningHourDefaultValues.unit.toString(),
};

export const flowCounterDefaults: ChannelSheetCellMappingType = {
    [ChannelSheetCol.MEASURABLE_MAX]: flowCounterDefaultValues.measurableMax.toString(),
    [ChannelSheetCol.MEASURABLE_MIN]: flowCounterDefaultValues.measurableMin.toString(),
    [ChannelSheetCol.DIGITAL_INPUT_FILTER]: flowCounterDefaultValues.digitalInputFilter.toString(),
};

export const digitalDefaults: ChannelSheetCellMappingType = {
    [ChannelSheetCol.DIGITAL_ON]: digitalAlarmDefaults.onStatus.toString(),
    [ChannelSheetCol.DIGITAL_OFF]: digitalAlarmDefaults.offStatus.toString(),
    [ChannelSheetCol.DIGITAL_INPUT_FILTER]: digitalDefaultRange.digitalInputFilter.toString(),
};

export const digitalMotorDefaults: ChannelSheetCellMappingType = {
    [ChannelSheetCol.DIGITAL_INPUT_FILTER]: digitalDefaultRange.digitalInputFilter.toString(),
    [ChannelSheetCol.SIGNAL_WIDTH]: digitalDefaultRange.signalWidth.toString(),
    [ChannelSheetCol.MOTOR_ENABLE]: motorAlarmDefaults.enable.toString(),
    [ChannelSheetCol.MOTOR_TYPE]: MotorChannelType[motorAlarmDefaults.motorType],
    [ChannelSheetCol.MOTOR_ALARM_GROUP_NO]: motorAlarmDefaults.alarmGroupNo.toString(),
    [ChannelSheetCol.MOTOR_REPOSE_GROUP1_NO]: motorAlarmDefaults.reposeGroup1No.toString(),
    [ChannelSheetCol.MOTOR_REPOSE_GROUP2_NO]: motorAlarmDefaults.reposeGroup2No.toString(),
    [ChannelSheetCol.MOTOR_DELAY]: motorAlarmDefaults.delay.toString(),
    [ChannelSheetCol.MOTOR_ALARM]: AlarmLevel[motorAlarmDefaults.alarm],
};

export const analogDefaults: ChannelSheetCellMappingType = {
    [ChannelSheetCol.MEASURABLE_MAX]: analogChannelDefaultValues.measurableMax.toString(),
    [ChannelSheetCol.MEASURABLE_MIN]: analogChannelDefaultValues.measurableMin.toString(),
    [ChannelSheetCol.ALARM_RECOVERY_HYSTERESIS]: analogChannelDefaultValues.alarmRecoveryHysteresis.toString(),
};

export const exhGasMeanDefaults: ChannelSheetCellMappingType = {
    [ChannelSheetCol.MEASURABLE_MAX]: exhaustDefaultValues.measurableMax.toString(),
    [ChannelSheetCol.MEASURABLE_MIN]: exhaustDefaultValues.measurableMin.toString(),
};

export const exhGasDevDefaults: ChannelSheetCellMappingType = {
    [ChannelSheetCol.MEASURABLE_MAX]: exhaustDefaultValues.measurableMax.toString(),
    [ChannelSheetCol.MEASURABLE_MIN]: exhaustDefaultValues.measurableMin.toString(),
    [ChannelSheetCol.ALARM_RECOVERY_HYSTERESIS]: exhaustDefaultValues.alarmRecoveryHysteresis.toString(),
};

export const HHEnableDefaults: ChannelSheetCellMappingType = {
    [ChannelSheetCol.HH_STRING]: hhAlarmDefaults.status.toString(),
    [ChannelSheetCol.HH_SETPOINT]: hhAlarmDefaults.setPoint.toString(),
    [ChannelSheetCol.HH_ALARM_GROUP_NO]: hhAlarmDefaults.alarmGroupNo.toString(),
    [ChannelSheetCol.HH_REPOSE_GROUP1_NO]: hhAlarmDefaults.reposeGroup1No.toString(),
    [ChannelSheetCol.HH_REPOSE_GROUP2_NO]: hhAlarmDefaults.reposeGroup2No.toString(),
    [ChannelSheetCol.HH_DELAY]: hhAlarmDefaults.delay.toString(),
    [ChannelSheetCol.HH_ALARM]: AlarmLevel[hhAlarmDefaults.alarm],
};

export const HEnableDefaults: ChannelSheetCellMappingType = {
    [ChannelSheetCol.H_STRING]: hAlarmDefaults.status.toString(),
    [ChannelSheetCol.H_SETPOINT]: hAlarmDefaults.setPoint.toString(),
    [ChannelSheetCol.H_ALARM_GROUP_NO]: hAlarmDefaults.alarmGroupNo.toString(),
    [ChannelSheetCol.H_REPOSE_GROUP1_NO]: hAlarmDefaults.reposeGroup1No.toString(),
    [ChannelSheetCol.H_REPOSE_GROUP2_NO]: hAlarmDefaults.reposeGroup2No.toString(),
    [ChannelSheetCol.H_DELAY]: hAlarmDefaults.delay.toString(),
    [ChannelSheetCol.H_ALARM]: AlarmLevel[hAlarmDefaults.alarm],
};

export const LLEnableDefaults: ChannelSheetCellMappingType = {
    [ChannelSheetCol.LL_STRING]: llAlarmDefaults.status.toString(),
    [ChannelSheetCol.LL_SETPOINT]: llAlarmDefaults.setPoint.toString(),
    [ChannelSheetCol.LL_ALARM_GROUP_NO]: llAlarmDefaults.alarmGroupNo.toString(),
    [ChannelSheetCol.LL_REPOSE_GROUP1_NO]: llAlarmDefaults.reposeGroup1No.toString(),
    [ChannelSheetCol.LL_REPOSE_GROUP2_NO]: llAlarmDefaults.reposeGroup2No.toString(),
    [ChannelSheetCol.LL_DELAY]: llAlarmDefaults.delay.toString(),
    [ChannelSheetCol.LL_ALARM]: AlarmLevel[llAlarmDefaults.alarm],
};

export const LEnableDefaultsExhGasMean: ChannelSheetCellMappingType = {
    [ChannelSheetCol.L_SETPOINT]: lAlarmDefaults.setPoint.toString(),
};

export const LEnableDefaults: ChannelSheetCellMappingType = {
    [ChannelSheetCol.L_STRING]: lAlarmDefaults.status.toString(),
    [ChannelSheetCol.L_SETPOINT]: lAlarmDefaults.setPoint.toString(),
    [ChannelSheetCol.L_ALARM_GROUP_NO]: lAlarmDefaults.alarmGroupNo.toString(),
    [ChannelSheetCol.L_REPOSE_GROUP1_NO]: lAlarmDefaults.reposeGroup1No.toString(),
    [ChannelSheetCol.L_REPOSE_GROUP2_NO]: lAlarmDefaults.reposeGroup2No.toString(),
    [ChannelSheetCol.L_DELAY]: lAlarmDefaults.delay.toString(),
    [ChannelSheetCol.L_ALARM]: AlarmLevel[lAlarmDefaults.alarm],
};

export const SensorFailEnableDefaults: ChannelSheetCellMappingType = {
    [ChannelSheetCol.SENSOR_FAILURE_STRING]: sensorFailureDefaults.status.toString(),
    [ChannelSheetCol.SENSOR_FAILURE_ALARM_GROUP_NO]: sensorFailureDefaults.alarmGroupNo.toString(),
    [ChannelSheetCol.SENSOR_FAILURE_REPOSE_GROUP1_NO]: sensorFailureDefaults.reposeGroup1No.toString(),
    [ChannelSheetCol.SENSOR_FAILURE_REPOSE_GROUP2_NO]: sensorFailureDefaults.reposeGroup2No.toString(),
    [ChannelSheetCol.SENSOR_FAILURE_DELAY]: sensorFailureDefaults.delay.toString(),
    [ChannelSheetCol.SENSOR_FAILURE_ALARM]: AlarmLevel[sensorFailureDefaults.alarm],
};

export const MotorAlarmEnableDefaults: ChannelSheetCellMappingType = {
    [ChannelSheetCol.MOTOR_ENABLE]: motorAlarmDefaults.enable.toString(),
    [ChannelSheetCol.MOTOR_TYPE]: MotorChannelType[motorAlarmDefaults.motorType],
    [ChannelSheetCol.MOTOR_ALARM_GROUP_NO]: motorAlarmDefaults.alarmGroupNo.toString(),
    [ChannelSheetCol.MOTOR_REPOSE_GROUP1_NO]: motorAlarmDefaults.reposeGroup1No.toString(),
    [ChannelSheetCol.MOTOR_REPOSE_GROUP2_NO]: motorAlarmDefaults.reposeGroup2No.toString(),
    [ChannelSheetCol.MOTOR_DELAY]: motorAlarmDefaults.delay.toString(),
    [ChannelSheetCol.MOTOR_ALARM]: AlarmLevel[motorAlarmDefaults.alarm],
};

export const DigitalAlarmEnableDefaults: ChannelSheetCellMappingType = {
    [ChannelSheetCol.DIGITAL_ON]: digitalAlarmDefaults.onStatus.toString(),
    [ChannelSheetCol.DIGITAL_OFF]: digitalAlarmDefaults.offStatus.toString(),
    [ChannelSheetCol.DIGITAL_ALARM_GROUP_NO]: digitalAlarmDefaults.alarmGroupNo.toString(),
    [ChannelSheetCol.DIGITAL_REPOSE_GROUP1_NO]: digitalAlarmDefaults.reposeGroup1No.toString(),
    [ChannelSheetCol.DIGITAL_REPOSE_GROUP2_NO]: digitalAlarmDefaults.reposeGroup2No.toString(),
    [ChannelSheetCol.DIGITAL_DELAY]: digitalAlarmDefaults.delay.toString(),
    [ChannelSheetCol.NORMAL_OPEN]: digitalAlarmDefaults.normalOpenFlag.toString(),
    [ChannelSheetCol.DIGITAL_ALARM]: AlarmLevel[digitalAlarmDefaults.alarm],
};

export const FeedbackAlarmEnableDefaults: ChannelSheetCellMappingType = {
    [ChannelSheetCol.FEEDBACK_ERROR_STRING]: feedbackAlarmDefaults.status.toString(),
    [ChannelSheetCol.FEEDBACK_ERROR_ALARM_GROUP_NO]: feedbackAlarmDefaults.alarmGroupNo.toString(),
    [ChannelSheetCol.FEEDBACK_ERROR_REPOSE_GROUP1_NO]: feedbackAlarmDefaults.reposeGroup1No.toString(),
    [ChannelSheetCol.FEEDBACK_ERROR_REPOSE_GROUP2_NO]: feedbackAlarmDefaults.reposeGroup2No.toString(),
    [ChannelSheetCol.FEEDBACK_ERROR_DELAY]: feedbackAlarmDefaults.delay.toString(),
    [ChannelSheetCol.FEEDBACK_ERROR_ALARM]: AlarmLevel[feedbackAlarmDefaults.alarm],
};

export const SystemAlarmEnableDefaults: ChannelSheetCellMappingType = {
    [ChannelSheetCol.SYSTEM_ALARM_ENABLE]: systemAlarmDefaults.enable.toString(),
    [ChannelSheetCol.SYSTEM_ALARM_GROUP_NO]: systemAlarmDefaults.alarmGroupNo.toString(),
    [ChannelSheetCol.SYSTEM_ALARM_REPOSE_GROUP1_NO]: systemAlarmDefaults.reposeGroup1No.toString(),
    [ChannelSheetCol.SYSTEM_ALARM_REPOSE_GROUP2_NO]: systemAlarmDefaults.reposeGroup2No.toString(),
    [ChannelSheetCol.SYSTEM_ALARM_DELAY]: systemAlarmDefaults.delay.toString(),
    [ChannelSheetCol.SYSTEM_ALARM_ALARM]: AlarmLevel[systemAlarmDefaults.alarm],
};
